import React, { Component } from "react";
import Highcharts from 'highcharts';
import { CircularProgress, Button, ButtonGroup } from '@material-ui/core';
import ViewUtils from '../ViewUtils';
import { REPORT_CONFIG } from './index';
import "./DashboardCharts.css";
import { BarChart, ViewWeek } from '@material-ui/icons';

const utils = new ViewUtils();

export const withChartLogic = (WrappedComponent, defaultType) => {
    return class extends Component {
        constructor(props) {
            super(props);
            this.chartRef = React.createRef();
            this.chart = null;
            this.lastResponse = null; 
            this.state = { 
                loading: false, 
                noData: false,
                chartType: defaultType === 'bar' ? 'column' : defaultType,
                kpi: null,
                hoverKpi: null 
            };
        }

        componentDidMount() { this.checkAndFetch(); }
        
        componentDidUpdate(prevProps, prevState) {
            const { reportName, FinancialYearID, QuarterID } = this.props;
            if (prevProps.reportName !== reportName || 
                prevProps.FinancialYearID !== FinancialYearID || 
                prevProps.QuarterID !== QuarterID) {
                this.checkAndFetch();
            }
            if (prevState.chartType !== this.state.chartType && this.lastResponse) {
                // Re-render using the un-cleansed cache so it can be re-cleansed safely
                this.processAndRender(this.lastResponse);
            }
        }

        checkAndFetch = () => {
            const { reportName, FinancialYearID } = this.props;
            if (reportName && REPORT_CONFIG[reportName] && FinancialYearID !== 0) {
                this.fetchData();
            }
        }

        toggleType = (type) => { this.setState({ chartType: type }); }

        fetchData = () => {
            const { FinancialYearID, reportName, QuarterID } = this.props;
            const config = REPORT_CONFIG[reportName];
            this.setState({ loading: true, noData: false });

            const params = [
                { Name: "FinancialYearID", Value: FinancialYearID }, 
                { Name: "ExportToExcel", Value: "" }, 
                { Name: "Filter", Value: "" }
            ];
            
            if (config.hasQuarterFilter) {
                params.push({ Name: "QuarterID", Value: QuarterID || 0 });
            }

            utils.invokeUrl(config.apiUrl + "?request=" + utils.doGetRequestJson(0, 0, params, true), (rawResponse) => {
                if (!rawResponse || rawResponse.length === 0) {
                    this.setState({ loading: false, noData: true, kpi: null, hoverKpi: null });
                    return;
                }
                this.lastResponse = rawResponse; // Save raw response for switching chart types
                this.processAndRender(rawResponse);
            }, () => this.setState({ loading: false, kpi: null, hoverKpi: null }));
        }

        processAndRender = (rawResponse) => {
            const { reportName, onDataLoaded } = this.props;
            const config = REPORT_CONFIG[reportName];
            const { chartType } = this.state;
            
            // --- UI DATA CLEANSING ---
            // Intercepts the data and strips the word "Structure" out of any labels
            const response = rawResponse.map(row => {
                const newRow = { ...row };
                if (newRow.Values) {
                    newRow.Values = String(newRow.Values).replace(/\bStructure\b/gi, '').replace(/\s{2,}/g, ' ').trim();
                }
                if (newRow.Category) {
                    newRow.Category = String(newRow.Category).replace(/\bStructure\b/gi, '').replace(/\s{2,}/g, ' ').trim();
                }
                return newRow;
            });
            // --------------------------
            
            let categories = response.map(o => String(o.Values || o.Category || "Unknown"));
            let series = [];
            let totalCount = 0;

            let kpiTotal = 0, kpiAchieved = 0, kpiTarget = 0;

            const isLine = chartType === 'line';
            const lineConfig = isLine ? {
                lineWidth: 3,
                marker: { enabled: true, radius: 4, symbol: 'circle' }
            } : {};

            if (chartType === 'pie') {
                series = [{
                    name: config.title,
                    type: 'pie',
                    data: response.map(o => {
                        const val = Math.abs(o[config.pieValueKey] || o.Total || 0);
                        totalCount += val;
                        return { name: String(o.Values || o.Category || "Unknown"), y: val };
                    })
                }];
            } else if (config.isMultiSeries) {
                
                response.forEach(o => {
                    kpiTotal += (o.TotalIndicators || 0);
                    kpiTarget += (o[config.targetKey] || 0);
                    kpiAchieved += (o[config.achievementKey] || 0);
                });

                if (response.length > 0 && response[0].TotalIndicators !== undefined) {
                    series.push({
                        name: 'Total Indicators', 
                        color: '#9c27b0', 
                        type: chartType,
                        ...lineConfig, 
                        data: response.map(o => o.TotalIndicators || 0)
                    });
                }

                series.push({ 
                    name: 'Target', 
                    color: '#00d4ff', 
                    type: chartType, 
                    ...lineConfig,
                    data: response.map(o => o[config.targetKey] || 0) 
                });
                
                series.push({ 
                    name: 'Actual', 
                    color: '#ffc107', 
                    type: chartType, 
                    ...lineConfig,
                    data: response.map(o => {
                        const v = o[config.achievementKey] || 0;
                        totalCount += v;
                        return v;
                    })
                });

                series.push({ 
                    name: config.performanceKey ? 'Perf %' : 'Variance', 
                    color: '#28a745', 
                    type: chartType, 
                    yAxis: 1, 
                    ...lineConfig,
                    tooltip: { valueSuffix: config.performanceKey ? ' %' : '' },
                    dataLabels: { 
                        enabled: true, 
                        format: '{y}' + (config.performanceKey ? '%' : ''), 
                        style: { color: '#28a745', textOutline: 'none' }
                    },
                    data: response.map(o => {
                        const val = o[config.performanceKey || config.varianceKey];
                        return val ? Number(val) : 0;
                    }) 
                });

            } else {
                series = [{ 
                    name: config.title, 
                    color: '#00d4ff', 
                    type: chartType,
                    ...lineConfig,
                    data: response.map(o => {
                        const v = o.Total || 0;
                        totalCount += v;
                        return v;
                    }) 
                }];
            }

            let kpiPerf = kpiTarget > 0 ? ((kpiAchieved / kpiTarget) * 100).toFixed(1) : 0;

            this.setState({ 
                loading: false, 
                hoverKpi: null,
                kpi: { total: kpiTotal, target: kpiTarget, achieved: kpiAchieved, perf: kpiPerf } 
            }, () => {
                this.renderHighchart(categories, series, config, response); // Pass cleansed response
            });
            
            // Pass cleansed response out to the Summary Table
            if (onDataLoaded) onDataLoaded(totalCount, response);
        }

        renderHighchart(categories, series, config, responseData) {
            const containerId = "chart-container-" + this.props.reportName;
            if (this.chart) { this.chart.destroy(); }

            const { chartType } = this.state;
            const isPie = chartType === 'pie';

            this.chart = Highcharts.chart(containerId, {
                colors: ['#00d4ff', '#ffc107', '#28a745', '#9c27b0', '#ff6b6b', '#4ecdc4', '#ffe66d'],

                chart: { 
                    type: chartType, 
                    height: '400px', 
                    backgroundColor: 'transparent'
                },
                title: { text: ' ' },
                xAxis: {
                    categories: categories,
                    visible: !isPie,
                    labels: { 
                        style: { color: '#ccc', fontSize: '10px' },
                        rotation: chartType === 'column' ? -45 : 0 
                    }
                },
                yAxis: isPie ? null : [
                    { 
                        title: { text: '' }, 
                        gridLineColor: '#333',
                        min: 0,
                        labels: { style: { color: '#ccc' } }
                    },
                    { 
                        title: { text: '' }, 
                        opposite: true, 
                        visible: !!config.isMultiSeries, 
                        gridLineWidth: 0,
                        min: 0, 
                        labels: {
                            format: '{value}%',
                            style: { color: '#28a745' } 
                        }
                    }
                ],
                legend: { 
                    enabled: this.props.showLegend,
                    layout: 'horizontal',
                    align: 'center',
                    verticalAlign: 'bottom', 
                    itemStyle: { color: '#E0E0E0' },
                    itemHoverStyle: { color: '#FFF' }
                },
                plotOptions: {
                    series: {
                        showInLegend: true,
                        cursor: 'pointer',
                        borderWidth: 0, 
                        dataLabels: { 
                            enabled: true,
                            style: {
                                color: '#ffffff',
                                textOutline: 'none',
                                fontWeight: '600',
                                fontSize: '10px'
                            }
                        },
                        point: {
                            events: {
                                mouseOver: (e) => {
                                    if (!responseData || !config.isMultiSeries) return;
                                    const category = isPie ? e.target.name : e.target.category;
                                    const rowData = responseData.find(r => String(r.Values || r.Category || "Unknown") === String(category));
                                    if (rowData) {
                                        this.setState({
                                            hoverKpi: {
                                                title: category, 
                                                total: rowData.TotalIndicators || 0,
                                                target: rowData[config.targetKey] || 0,
                                                achieved: rowData[config.achievementKey] || 0,
                                                perf: rowData[config.performanceKey || config.varianceKey] || 0
                                            }
                                        });
                                    }
                                },
                                mouseOut: () => {
                                    this.setState({ hoverKpi: null }); 
                                },
                                click: (e) => {
                                    const val = isPie ? e.point.name : e.point.category;
                                    if(this.props.onPointClick) this.props.onPointClick(val);
                                }
                            }
                        }
                    },
                    pie: {
                        showInLegend: true,
                        allowPointSelect: true,
                        dataLabels: {
                            enabled: true,
                            connectorWidth: 1,
                            format: '<b>{point.name}</b>: {point.y}',
                            style: { textOutline: 'none', color: '#fff' }
                        }
                    },
                    column: {
                        pointPadding: 0.1,
                        groupPadding: 0.15 
                    },
                    bar: {
                        pointPadding: 0.1,
                        groupPadding: 0.15 
                    }
                },
                series: series,
                credits: { enabled: false }
            });
        }

        render() {
            const { loading, noData, chartType, kpi, hoverKpi } = this.state;
            const isPie = defaultType === 'pie';
            const isLine = defaultType === 'line';
            
            const config = REPORT_CONFIG[this.props.reportName];
            const targetLabel = config && config.hasQuarterFilter ? "Quarter Target" : "Annual Target";
            
            const activeKpi = hoverKpi || kpi;
            const isHovering = !!hoverKpi;
            
            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    {!isPie && !isLine && !loading && !noData && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '8px' }}>
                            <ButtonGroup className="chart-switcher-pills icon-switcher" variant="contained">
                                <Button 
                                    className={chartType === 'column' ? 'active' : ''} 
                                    onClick={(e) => { e.stopPropagation(); this.toggleType('column'); }}
                                    title="Vertical View"
                                >
                                    <BarChart style={{ fontSize: 20 }} />
                                </Button>
                                <Button 
                                    className={chartType === 'bar' ? 'active' : ''} 
                                    onClick={(e) => { e.stopPropagation(); this.toggleType('bar'); }}
                                    title="Horizontal View"
                                >
                                    <ViewWeek style={{ fontSize: 20, transform: 'rotate(90deg)' }} />
                                </Button>
                                <Button 
                                    className={chartType === 'line' ? 'active' : ''} 
                                    onClick={(e) => { e.stopPropagation(); this.toggleType('line'); }}
                                    title="Line View"
                                >
                                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>~</span>
                                </Button>
                            </ButtonGroup>
                        </div>
                    )}
                    
                    {kpi && config && config.isMultiSeries && !loading && !noData && !this.props.isExpanded && (
                        <div className="mb-3" style={{ 
                            background: isHovering ? 'rgba(0, 212, 255, 0.08)' : 'rgba(255,255,255,0.05)', 
                            borderRadius: '8px', 
                            padding: '10px', 
                            border: isHovering ? '1px solid #00d4ff' : '1px solid transparent', 
                            transition: 'all 0.3s ease' 
                        }}>
                            {isHovering && <div className="text-center text-info mb-2 font-weight-bold" style={{textTransform: 'uppercase', letterSpacing: '1px', fontSize: '12px'}}>{activeKpi.title}</div>}
                            {!isHovering && <div className="text-center text-muted mb-2 font-weight-bold" style={{textTransform: 'uppercase', letterSpacing: '1px', fontSize: '10px'}}>Overall Dashboard Summary</div>}
                            
                            <div className="d-flex justify-content-around">
                                <div className="text-center">
                                    <div style={{ fontSize: '11px', color: '#ccc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Indicators</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#9c27b0' }}>{activeKpi.total}</div>
                                </div>
                                <div className="text-center">
                                    <div style={{ fontSize: '11px', color: '#ccc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{targetLabel}</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#00d4ff' }}>{activeKpi.target}</div>
                                </div>
                                <div className="text-center">
                                    <div style={{ fontSize: '11px', color: '#ccc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Achieved</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffc107' }}>{activeKpi.achieved}</div>
                                </div>
                                <div className="text-center">
                                    <div style={{ fontSize: '11px', color: '#ccc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Performance</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: activeKpi.perf >= 100 ? '#28a745' : '#ff6b6b' }}>{activeKpi.perf}%</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div style={{ position: 'absolute', left: '50%', top: '45%', zIndex: 10 }}>
                            <CircularProgress size={30} />
                        </div>
                    )}

                    {noData && !loading && (
                        <div style={{ textAlign: 'center', paddingTop: '100px', color: '#888' }}>No data found.</div>
                    )}

                    <div id={"chart-container-" + this.props.reportName} style={{ width: '100%' }}>
                        <WrappedComponent {...this.props} chartRef={this.chartRef} />
                    </div>
                </div>
            );
        }
    }
}